import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query'
import { clientService } from '@/services/ClientService'
import { clientKeys, proposalKeys } from '@/hooks/queryKeys'
import type { ClientDto } from '@lib/dto/client.dto'
import type {
  CreateClientInput,
  UpdateClientInput,
} from '@lib/input/client.input'

/**
 * Client write operations.
 *
 * Grouped in one file rather than split one-hook-per-file: they share the same
 * cache-invalidation rule (any client write invalidates both the lists and that
 * client's detail), and keeping that rule in a single place is what stops the
 * three from drifting apart.
 */

/** Invalidate every client list plus one client's detail. */
function useInvalidateClients(): (id?: string) => void {
  const queryClient = useQueryClient()
  return (id?: string) => {
    void queryClient.invalidateQueries({ queryKey: clientKeys.lists() })
    if (id) {
      void queryClient.invalidateQueries({ queryKey: clientKeys.detail(id) })
    }
  }
}

export function useCreateClient(): UseMutationResult<
  ClientDto,
  Error,
  CreateClientInput
> {
  const invalidate = useInvalidateClients()
  return useMutation({
    mutationFn: (input: CreateClientInput) => clientService.create(input),
    onSuccess: (client) => invalidate(client.id),
  })
}

interface UpdateClientVariables {
  id: string
  input: UpdateClientInput
}

export function useUpdateClient(): UseMutationResult<
  ClientDto,
  Error,
  UpdateClientVariables
> {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateClients()
  return useMutation({
    mutationFn: ({ id, input }: UpdateClientVariables) =>
      clientService.update(id, input),
    onSuccess: (client) => {
      invalidate(client.id)
      // Proposals carry the client's name, and a draft prints it and the
      // home count on its cover.
      void queryClient.invalidateQueries({ queryKey: proposalKeys.all })
    },
  })
}

interface SetArchivedVariables {
  id: string
  archived: boolean
}

export function useArchiveClient(): UseMutationResult<
  ClientDto,
  Error,
  SetArchivedVariables
> {
  const queryClient = useQueryClient()
  const invalidate = useInvalidateClients()
  return useMutation({
    mutationFn: ({ id, archived }: SetArchivedVariables) =>
      clientService.setArchived(id, archived),
    onSuccess: (client) => {
      invalidate(client.id)
      // A proposal prints the client's name, the same as an edit does.
      void queryClient.invalidateQueries({ queryKey: proposalKeys.all })
    },
  })
}
